import { ResourceRoles } from "enums/ResourceRolesEnum";
import AdminLayout from "layouts/Admin";
import PrivateRoutes from "layouts/PrivateRoutes";
import RequireRole from "layouts/RequireRole";
import { useEffect, useMemo } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Analysis from "views/Analysis";
import CargaMassiva from "views/CargaMassiva";
import ConsolidatedWorker from "views/List/ConsolidatedWorkers";

import Dashboard from "views/Dashboard/Dashboard";
import DashboardSupplier from "views/Dashboard/DashboardSupplier";
import DashboardWorker from "views/Dashboard/DashboardWorker";
import ErrorPage from "views/Error/ErrorPage";
import Unauthorized from "views/Unauthorized/Unauthorized";
import Sows from "views/List/SOWs";

import SowsAdm from "views/List/SOWsAdm";

import Subcontractors from "views/List/Subcontractors";
import Suppliers from "views/List/Suppliers";
import UserProfile from "views/UserProfile/UserProfile";

import Workers from "views/List/Workers";

import useHasRole from "hooks/useHasRole";
import { useSubscription } from "store/auth/auth-store";

import Users from "views/Users";
import { RolesEnum } from "utils/@types";
import { LatestApprovedAnalysis } from "views/Analysis/LatestApprovedAnalysis/LatestApprovedAnalysis";
import { LatestRejectedAnalysis } from "views/Analysis/LatestRejectedAnalysis/LatestRejectedAnalysis";
import { LatestPendingAnalysis } from "views/Analysis/LatestPendingAnalysis/LatestPendingAnalysis";
import SupplierProfile from "views/Profiles/Supplier";
import WorkerProfile from "views/Profiles/Worker";
import SowProfile from "views/Profiles/Sow";
import SowAdmProfile from "views/Profiles/SowAdm";
import SubcontractorProfile from "views/Profiles/Subcontractors";
import ConsolidatedWorkerProfile from "views/Profiles/ConsolidatedWorker";
import Equipments from "views/Equipments";
import WorkersAcelen from "views/List/WorkersAcelen";
import ActivityItemSettingsTabs from "views/ActivityItemSettingsTabs";
import SupplierDocumentsProfile from "views/Profiles/SupplierDocuments";
import PowerBIReport from "views/PowerBi";
import { useClientConfig } from "hooks/useClientConfig";
import ReleaseNotes from "views/ReleaseNotes";
import LoadingScreen from "components/UtilsComponent/LoadingScreen";
import AccessDenied from "views/AccessDenied/AccessDenied";
import keycloak from "configs/keycloak";
import Documentation from "views/Documentation";
import BeltrameReportsPage from "views/Relatorio/BeltrameReports";
import ReportPage from "views/Relatorio/ReportPage";

export default function AppRoutes(): JSX.Element {
  const { hasResourceRole, hasRealmRole } = useHasRole();
  const { pathname } = useLocation();
  const {
    isItemActive,
    featuresConfigsActiveByRole,
    isLoadingClientConfigSidebar,
  } = useClientConfig();

  const { isKuo, isBeltrame, isAcelen } = useSubscription();
  const isStratesysUser = keycloak.hasRealmRole("stratesys");

  useEffect(() => {
    const scrollTop = () => {
      const container = document.querySelector(".ps");
      if (container) {
        container.scrollTop = 0;
      }
    };

    scrollTop();
  }, [pathname]);

  const filteredBuyerRoutes = hasRealmRole(["Reports", "superUser"])
    ? featuresConfigsActiveByRole(["buyer", "reports"])
    : featuresConfigsActiveByRole(["buyer"]);

  const filteredSupplierRoutes = hasResourceRole([
    RolesEnum.SUPPLIER_PRIMARY_CONTACT,
    RolesEnum.STRATESYS,
  ])
    ? featuresConfigsActiveByRole(["supplier", "suppliers - primary contact"])
    : featuresConfigsActiveByRole(["supplier"]);

  const filteredWorkerRoutes = featuresConfigsActiveByRole(["worker"]);

  const routeMap: Record<ResourceRoles, JSX.Element> = {
    [ResourceRoles.BUYER]: (
      <Routes>
        <Route path="/" element={<AdminLayout routes={filteredBuyerRoutes} />}>
          <Route path="profile" element={<UserProfile />} />

          <Route path="release-notes" element={<ReleaseNotes />} />

          {isItemActive("users") && <Route path="users" element={<Users />} />}

          {isItemActive("dashboard") && (
            <>
              <Route path="dashboard" element={<Dashboard />} />
              <Route
                path="latestApprovedAnalysis"
                element={<LatestApprovedAnalysis />}
              />
              <Route
                path="latestPendingAnalysis"
                element={<LatestPendingAnalysis />}
              />
              <Route
                path="latestRejectedAnalysis"
                element={<LatestRejectedAnalysis />}
              />
            </>
          )}

          {isItemActive("suppliers") && (
            <Route path="suppliers">
              <Route
                index
                element={<Navigate to="/app/suppliers/list" replace />}
              />
              <Route path="list" element={<Suppliers />} />
              <Route path="profile" element={<SupplierProfile />} />
            </Route>
          )}

          {isItemActive("workers") && (
            <Route path="workers">
              <Route
                index
                element={<Navigate to="/app/workers/list" replace />}
              />
              <Route
                path="list"
                element={isAcelen ? <WorkersAcelen /> : <Workers />}
              />
              <Route path="profile" element={<WorkerProfile />} />
            </Route>
          )}

          {isItemActive("equipments") && (
            <Route path="equipments" element={<Equipments />} />
          )}

          {isItemActive("activity-item-settings") && (
            <Route
              path="activity-item-settings/list"
              element={<ActivityItemSettingsTabs />}
            />
          )}

          <Route
            path="consolidatedworkers"
            element={<RequireRole roles={["consolidatedWorkers"]} />}
          >
            <Route
              index
              element={<Navigate to="/app/consolidatedworkers/list" replace />}
            />
            <Route path="list" element={<ConsolidatedWorker />} />
            <Route path="profile" element={<ConsolidatedWorkerProfile />} />
          </Route>

          {isItemActive("subcontractors") && (
            <Route
              path="subcontractors"
              element={<RequireRole roles={["subcontractors"]} />}
            >
              <Route
                index
                element={<Navigate to="/app/subcontractors/list" replace />}
              />
              <Route path="list" element={<Subcontractors />} />
              <Route path="profile" element={<SubcontractorProfile />} />
            </Route>
          )}

          {isItemActive("sows") && (
            <Route path="sows" element={<RequireRole roles={["sowProfile"]} />}>
              <Route index element={<Navigate to="/app/sows/list" replace />} />
              <Route path="list" element={<Sows />} />
              <Route path="profile" element={<SowProfile />} />
            </Route>
          )}

          {isItemActive("sows-admin") && (
            <Route
              path="sowsAdm"
              element={<RequireRole roles={["sowProfile"]} />}
            >
              <Route
                index
                element={<Navigate to="/app/sowsAdm/list" replace />}
              />
              <Route path="list" element={<SowsAdm />} />
              <Route path="profile" element={<SowAdmProfile />} />
            </Route>
          )}

          {isItemActive("reports") && (
            <Route
              path="reports"
              element={<RequireRole roles={["sowProfile"]} />}
            >
              <Route
                index
                element={<Navigate to="/app/reports/list" replace />}
              />
              <Route path="list" element={<ReportPage />} />
            </Route>
          )}

          {isBeltrame && (
            <Route
              path="workflowStatus"
              element={<RequireRole roles={["sowProfile"]} />}
            >
              <Route
                index
                element={<Navigate to="/app/workflowStatus/list" replace />}
              />
              <Route path="list" element={<BeltrameReportsPage />} />
            </Route>
          )}

          {isItemActive("external-dashboard") && (
            <Route path="external-dashboard" element={<PowerBIReport />} />
          )}

          <Route path="data" element={<RequireRole roles={["cargaMassiva"]} />}>
            <Route index element={<Navigate to="/app/data/upload" replace />} />
            <Route path="upload" element={<CargaMassiva />} />
          </Route>

          <Route path="unauthorized" element={<Unauthorized />} />
          <Route path="/access-denied" element={<AccessDenied />} />

          <Route path="*" element={<ErrorPage />} />
        </Route>
      </Routes>
    ),
    [ResourceRoles.SUPPLIER]: (
      <Routes>
        <Route
          path="/"
          element={<AdminLayout routes={filteredSupplierRoutes} />}
        >
          <Route path="profile" element={<UserProfile />} />

          {isItemActive("users") && <Route path="users" element={<Users />} />}

          {isItemActive("dashboard") && (
            <>
              <Route path="dashboard" element={<DashboardSupplier />} />
              <Route
                path="latestApprovedAnalysis"
                element={<LatestApprovedAnalysis />}
              />
              <Route
                path="latestPendingAnalysis"
                element={<LatestPendingAnalysis />}
              />
              <Route
                path="latestRejectedAnalysis"
                element={<LatestRejectedAnalysis />}
              />
            </>
          )}

          {isItemActive("suppliers") && (
            <Route path="suppliers">
              <Route
                index
                element={<Navigate to="/app/suppliers/list" replace />}
              />
              <Route path="profile" element={<SupplierProfile />} />
            </Route>
          )}

          {isItemActive("workers") && (
            <Route path="workers">
              <Route
                index
                element={<Navigate to="/app/workers/list" replace />}
              />
              <Route
                path="list"
                element={isAcelen ? <WorkersAcelen /> : <Workers />}
              />
              <Route path="profile" element={<WorkerProfile />} />
            </Route>
          )}

          {isItemActive("equipments") && (
            <Route path="equipments" element={<Equipments />} />
          )}

          <Route
            path="consolidatedworkers"
            element={<RequireRole roles={["consolidatedWorkers"]} />}
          >
            <Route
              index
              element={<Navigate to="/app/consolidatedworkers/list" replace />}
            />
            <Route path="list" element={<ConsolidatedWorker />} />
            <Route path="profile" element={<ConsolidatedWorkerProfile />} />
          </Route>

          {isItemActive("subcontractors") && (
            <Route
              path="subcontractors"
              element={<RequireRole roles={["subcontractors"]} />}
            >
              <Route
                index
                element={<Navigate to="/app/subcontractors/list" replace />}
              />
              <Route path="list" element={<Subcontractors />} />
              <Route path="profile" element={<SubcontractorProfile />} />
            </Route>
          )}

          {isItemActive("sows") && (
            <Route path="sows">
              <Route index element={<Navigate to="/app/sows/list" replace />} />
              <Route path="list" element={<Sows />} />
              <Route path="profile" element={<SowProfile />} />
            </Route>
          )}

          {isKuo ? (
            <Route
              path="sowsAdm"
              element={<RequireRole roles={["sowProfile"]} />}
            >
              <Route
                index
                element={<Navigate to="/app/sowsAdm/list" replace />}
              />
              <Route path="list" element={<SowsAdm />} />
              <Route path="profile" element={<SowAdmProfile />} />
            </Route>
          ) : (
            isBeltrame && (
              <Route path="sowsAdm/*" element={<SupplierDocumentsProfile />} />
            )
          )}

          {isItemActive("reports") && (
            <Route
              path="reports"
              element={<RequireRole roles={["sowProfile"]} />}
            >
              <Route
                index
                element={<Navigate to="/app/reports/list" replace />}
              />
              <Route path="list" element={<ReportPage />} />
            </Route>
          )}

          {isBeltrame && (
            <Route
              path="workflowStatus"
              element={<RequireRole roles={["sowProfile"]} />}
            >
              <Route
                index
                element={<Navigate to="/app/workflowStatus/list" replace />}
              />
              <Route path="list" element={<BeltrameReportsPage />} />
            </Route>
          )}

          {isItemActive("external-dashboard") && (
            <Route path="external-dashboard" element={<PowerBIReport />} />
          )}

          <Route path="data" element={<RequireRole roles={["cargaMassiva"]} />}>
            <Route index element={<Navigate to="/app/data/upload" replace />} />
            <Route path="upload" element={<CargaMassiva />} />
          </Route>
          <Route path="unauthorized" element={<Unauthorized />} />
          <Route path="/access-denied" element={<AccessDenied />} />

          <Route path="*" element={<ErrorPage />} />
        </Route>
      </Routes>
    ),
    [ResourceRoles.WORKER]: (
      <Routes>
        <Route path="/" element={<AdminLayout routes={filteredWorkerRoutes} />}>
          <Route path="profile" element={<UserProfile />} />

          {isItemActive("dashboard") && (
            <Route path="dashboard" element={<DashboardWorker />} />
          )}

          {isItemActive("workers") && (
            <Route path="workers">
              <Route path="profile" element={<WorkerProfile />} />
            </Route>
          )}

          <Route path="analysis">
            <Route
              index
              element={<Navigate to="/app/analysis/list" replace />}
            />
            <Route path="list" element={<Analysis />} />
          </Route>
          <Route path="*" element={<Unauthorized />} />
        </Route>
      </Routes>
    ),
  };

  const routes = useMemo(() => {
    for (const role of Object.values(ResourceRoles)) {
      if (hasResourceRole([role])) {
        return (
          <Routes>
            {isStratesysUser && (
              <Route path="/documentation" element={<Documentation />} />
            )}
            <Route element={<PrivateRoutes />}>
              <Route path="*" element={routeMap[role]} />
            </Route>
          </Routes>
        );
      }
    }

    return (
      <Routes>
        <Route element={<PrivateRoutes />}>
          <Route path="unauthorized" element={<Unauthorized />} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="*" element={<ErrorPage />} />
        </Route>
      </Routes>
    );
  }, [hasResourceRole, routeMap]);

  return isLoadingClientConfigSidebar ? <LoadingScreen /> : routes;
}
